import type { HTMLAttributes, ReactNode, Ref } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import type { AlertTone } from '../Alert/Alert'
import { cx } from '../cx'
import styles from './Card.module.css'

type CardBase = Omit<HTMLAttributes<HTMLElement>, 'style' | 'title'> & {
  className?: string
  ref?: Ref<HTMLElement>
}

type CardLink = { as: 'a'; href: string }
type CardBox = { as?: 'div'; href?: never; interactive?: boolean }

export type CardProps = CardBase &
  (CardLink | CardBox) &
  (
    | {
        variant?: 'default' | 'flat' | 'inset'
        header?: ReactNode
        footer?: ReactNode
        children: ReactNode
      }
    | {
        variant: 'stat'
        label: ReactNode
        value: ReactNode
        delta?: ReactNode
        deltaTone?: AlertTone
        children?: never
      }
  )

// Every prop of every branch, optional: the union is enforced for callers, and
// the implementation reads whichever branch it was given.
type AnyCardProps = CardBase & {
  as?: 'a' | 'div'
  href?: string
  interactive?: boolean
  variant?: 'default' | 'flat' | 'inset' | 'stat'
  header?: ReactNode
  footer?: ReactNode
  label?: ReactNode
  value?: ReactNode
  delta?: ReactNode
  deltaTone?: AlertTone
  children?: ReactNode
}

export function Card(props: CardProps) {
  const {
    as,
    href,
    interactive,
    variant,
    className,
    ref,
    header,
    footer,
    label,
    value,
    delta,
    deltaTone,
    children,
    ...domProps
  } = props as AnyCardProps

  const body =
    variant === 'stat' ? (
      <>
        <span className={styles.statLabel}>{label}</span>
        <span className={styles.statValue}>{value}</span>
        {delta != null && (
          <span className={styles.statDelta} data-tone={deltaTone}>
            {delta}
          </span>
        )}
      </>
    ) : (
      <>
        {header != null && <div className={styles.header}>{header}</div>}
        {children}
        {footer != null && <div className={styles.footer}>{footer}</div>}
      </>
    )

  const shared = {
    ...domProps,
    className: cx(styles.card, className),
    'data-variant': variant ?? 'default',
    'data-interactive': as === 'a' || interactive ? '' : undefined,
  }

  if (as === 'a') {
    return (
      <RouterLink
        {...shared}
        ref={ref as Ref<HTMLAnchorElement>}
        to={href as string}
      >
        {body}
      </RouterLink>
    )
  }
  return (
    <div {...shared} ref={ref as Ref<HTMLDivElement>}>
      {body}
    </div>
  )
}
